<template>
  <div class="event-teams-grid">
    <grid
      ref="grid"
      :add-checkmarks="addCheckmarks"
      :list-url="listUrl"
      :serialize-to-jsona="true"
      :row-class-fn="gridRowClassFn"
      @meta-loaded="onMetaLoaded"
      @header-action="onHeaderAction"
    >
      <template #before-header>
        <slot name="grid-before-header" />
      </template>
      <template #additional-row="{ item }">
        <tr
          v-if="showMembers"
          class="members"
        >
          <td v-if="addCheckmarks" />
          <td />
          <td
            :colspan="membersColspan"
            class="members"
          >
            <table class="table table-striped table-bordered table-hover table-sm">
              <thead>
                <tr>
                  <th
                    v-for="column of memberTableColumns"
                    :key="`col-${column.name}`"
                  >
                    {{ column.title }}
                  </th>
                  <th class="table-actions" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(member, memberIdx) in item.members"
                  :key="`member-${memberIdx}`"
                >
                  <td
                    v-for="column of memberTableColumns"
                    :key="`member-col-${memberIdx}-${column.name}`"
                  >
                    {{ member[column.name] }}
                  </td>
                  <td class="table-actions">
                    <item-action-view
                      :item="member"
                      :edit-action-as-link="false"
                      @item-action="triggerItemAction"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
          <td />
        </tr>
      </template>
    </grid>
    <teleport
      to="#vue-portal-target"
      append
    >
      <candidate-modal
        ref="candidateModal"
        modal-title="Edit candidate"
        @success="candidateModalSuccess"
      />
      <participant-invite-modal
        ref="inviteParticipantModal"
        @success="inviteModalSuccess"
      />
      <candidate-details-modal ref="candidateDetailsModal" />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import {computed, ref, useTemplateRef} from "vue"
import Grid from "./grid.vue"
import CandidateModal from "./candidate-modal.vue"
import CandidateDetailsModal from "./candidate-details-modal.vue"
import ItemActionView from "./item-action-view.vue"
import ParticipantInviteModal from "./participant-invite-modal.vue"
import {isNonEmptyString, isPresent} from "../js-utils"
import type {ButtonAction, GridHeaderAction} from "./types"

interface GridColumn {
  name: string;
  title: string;
}

defineProps({
  listUrl: {default: null, type: String},
  addCheckmarks: {default: false, type: Boolean},
})

const emit = defineEmits<{
  (e: "meta-loaded", meta: any): void;
  (e: "contentschanged"): void;
}>()

const memberTableColumns = ref<GridColumn[]>([])

const grid = useTemplateRef<InstanceType<typeof Grid>>("grid")
const candidateModal = useTemplateRef<InstanceType<typeof CandidateModal>>("candidateModal")
const inviteParticipantModal = useTemplateRef<InstanceType<typeof ParticipantInviteModal>>("inviteParticipantModal")
const candidateDetailsModal = useTemplateRef<InstanceType<typeof CandidateDetailsModal>>("candidateDetailsModal")

const showMembers = computed((): boolean => {
  return memberTableColumns.value.length > 0
})

const membersColspan = computed((): number => {
  const aGrid = grid.value
  if (aGrid) {
    return aGrid.columns.length - 2
  }

  return 0
})

const onMetaLoaded = (meta: any): void => {
  if (Array.isArray(meta.member_table_columns)) {
    memberTableColumns.value = meta.member_table_columns
  }
  emit('meta-loaded', meta)
}

const showEditCandidateModal = (editUrl: string): void => {
  candidateModal.value?.showModal(`${editUrl}.json`, null)
}

const candidateModalSuccess = (): void => {
  candidateModal.value?.hideModal()
  // reload candidates table view
  // this.$refs.candidatesGrid.reload()
  grid.value?.reload()
  // this.$emit('contentschanged')
}

const onHeaderAction = (actionOrName: string | GridHeaderAction | ButtonAction, url?: string): void => {
  if (typeof actionOrName !== 'string' || url === undefined) {
    return
  }

  const actionName = actionOrName
  const actionUrl = url
  if (actionName == 'select-teams') {
    selectTeams(actionUrl)
  }

  if (actionName === 'send-invitations') {
    inviteParticipant(actionUrl)
    return
  }

  if (actionName === 'send-reimbursement' || actionName === 're-send-reimbursement') {
    if (confirm('Are you sure?')) {
      grid.value?.performRequest(actionUrl, {}, {}, (status, responseData) => {
        if (status) {
          if (isPresent(responseData) && isPresent(responseData.error)) {
            grid.value?.showError(responseData.error)
          } else {
            emit('contentschanged')
          }
        }
      })
    }
  }
}

const inviteParticipant = (inviteUrl: string): void => {
  inviteParticipantModal.value?.showModal(inviteUrl, null)
}

const inviteReimbursementParticipant = (inviteUrl: string): void => {
  inviteParticipantModal.value?.showModal(inviteUrl, 2)
}

const inviteModalSuccess = (inviteUrl: string | null, messagingType: number): void => {
  inviteParticipantModal.value?.hideModal()
  if (inviteUrl === null) {
    return
  }

  grid.value?.performRequest(inviteUrl, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    message_type_id: messagingType,
  }, {}, (status, responseData) => {
    if (status) {
      if (isPresent(responseData) && isPresent(responseData.error)) {
        // this.error = response.data.error
        grid.value?.showError(responseData.error)
      } else {
        // self.$refs.grid.reload()
        emit('contentschanged')
      }
    }
  })
}

const selectTeams = (url: string): void => {
  const selectedTeamIds = grid.value?.selectedItemIds || []
  if (selectedTeamIds.length === 0) {
    alert('Please select at least one team')
    return
  }

  grid.value?.performRequest(url, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    selected_team_ids: selectedTeamIds,
  }, {}, (status, responseData) => {
    if (status) {
      if (isPresent(responseData) && isPresent(responseData.error)) {
        // this.error = response.data.error
        grid.value?.showError(responseData.error)
      } else {
        // self.$refs.grid.reload()
        emit('contentschanged')
      }
    }
  })
}

const triggerItemAction = (item: any, action: any): void => {
  if (action == 'edit') {
    showEditCandidateModal(item.links.edit)
  } else {
    if (action.type == 'action' && action.name == 'edit' && action.link == 'edit' && isNonEmptyString(item.links.edit)) {
      showEditCandidateModal(item.links.edit)
    } else if (isPresent(action.type) && action.type == 'invite') {
      inviteParticipant(action.url)
    } else if (isPresent(action.type) && action.type == 'invite-reimbursement') {
      inviteReimbursementParticipant(action.url)
    } else if (action.type === 'view-member-details') {
      candidateDetailsModal.value?.showModal(action.url)
    } else if (action.type === 'approve-team-member') {
      approveTeamMember(action.url)
    } else {
      grid.value?.triggerItemAction(item, action)
    }
  }
}

const reload = (): void => {
  grid.value?.reload()
}

const approveTeamMember = (url: string): void => {
  grid.value?.performRequest(url, {}, {method: 'POST'}, (status, responseData) => {
    if (status) {
      if (isPresent(responseData) && isPresent(responseData.error)) {
        grid.value?.showError(responseData.error)
      } else {
        grid.value?.reload()
      }
    }
  })
}

const gridRowClassFn = (_item: any): string => {
  return 'table-info'
}

defineExpose({
  reload,
})

</script>
